"use client";
import Cookies from "js-cookie";
import { createContext, useContext, useLayoutEffect, useState } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { Theme, ThemeCountry } from "@/enums";
import { Loader } from "@/components/common";

const ThemeContext = createContext<Theme | "">("");

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const country =
    searchParams.get("country") || process.env.NEXT_PUBLIC_COUNTRY;
  const resetToken = searchParams.get("token");

  const domain = typeof window !== "undefined" && window?.location?.origin;

  const [theme, setTheme] = useState<Theme | "">("");
  const [isLoading, setIsLoading] = useState(true); // Loading state

  const updateTheme = (theme: Theme) => {
    setTheme(theme);
    Cookies.set("theme", theme);
    setIsLoading(false);
  };

  const cookieTheme = (Cookies.get("theme") as Theme) || Theme.UZ; // Fallback to Theme.UZ

  useLayoutEffect(() => {
    const setThemeAndPushState = (theme: Theme, countryParam: string) => {
      updateTheme(theme);
      router.push(
        `${pathname}?country=${countryParam}${
          resetToken ? `&token=${resetToken}` : ""
        }`,
      );
    };

    if (domain) {
      if (domain.includes("boxette.uz") || country === ThemeCountry.UZ) {
        updateTheme(Theme.UZ);
        return;
      }
      if (domain.includes("boxette.ge") || country === ThemeCountry.GEORGIA) {
        updateTheme(Theme.GEORGIA);
        return;
      }
      if (domain.includes("easyxpress.com.ua") || country === ThemeCountry.UK) {
        updateTheme(Theme.UK);
        return;
      } else {
        setThemeAndPushState(
          cookieTheme,
          ThemeCountry[theme.split("-")[0].toUpperCase()],
        );
      }
    }
  }, [country, pathname, searchParams, domain, cookieTheme]);

  return (
    <ThemeContext.Provider value={theme}>
      {isLoading ? (
        <div className="global-loader">
          <Loader />
        </div>
      ) : (
        <div className={theme}>{children}</div>
      )}
    </ThemeContext.Provider>
  );
}
